import Typography from '@material-ui/core/Typography';
import { FC, ReactElement, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { ReactComponent as DeleteIcon } from '~/ui/assets/images/delete.svg';
import variables from '~/ui/assets/styles/colors.module.scss';
import ButtonIcon from '~/ui/components/common/ButtonIcon';

import AllocationClientRow from '../../../shared/components/AllocationClientRow/AllocationClientRow';
import MemoField from '../../../shared/components/MemoField/MemoField';
import TeamsField from '../../../shared/components/TeamField/TeamsField';
import layoutStyles from '../../../shared/components/EditAllocationFormLayout/EditAllocationFormLayout.module.scss';
import { LocationGroupWithAllocation } from '../../../shared/model/types';
import { ILocationGroupClient } from '~/services/api/clientAllocation/types';
import { useAddClientAllocationContext } from '../../context/AddClientAllocationContext';
import useClientAvailabilityMessages from '../../../EditClientAllocation/hooks/validateAllocations/useClientAvailabilityMessages';
import { IAddAllocationFormValues } from '../../types';

interface IProps {
  field: LocationGroupWithAllocation & { id: string };
  fieldIdx: number;
  teamMemberIdx: number;
  memberId: number;
  showGroupName: boolean;
  onRemoveClient: (groupId: number, clientId: number) => void;
}

interface IClientRowProps {
  client: ILocationGroupClient;
  memberId: number;
  teamMemberIdx: number;
  fieldIdx: number;
  locationGroupId: number;
  isGlobalUser: boolean;
  onRemove: (groupId: number, clientId: number) => void;
}

const LocationGroupClientRow: FC<IClientRowProps> = ({
  client,
  memberId,
  teamMemberIdx,
  fieldIdx,
  locationGroupId,
  isGlobalUser,
  onRemove,
}) => {
  const { alertMessage, noteMessage } = useClientAvailabilityMessages({
    clientId: client.id,
    memberId,
    teamMemberIdx,
  });

  return (
    <AllocationClientRow
      client={{
        clientId: client.id,
        photo: client.photo,
        name: client.name,
        isResolved: false,
        isCrisis: false,
      }}
      alertMessage={alertMessage}
      noteMessage={noteMessage}
      actions={
        <ButtonIcon onClick={() => onRemove(locationGroupId, client.id)}>
          <DeleteIcon width={20} color={variables.colorRed} />
        </ButtonIcon>
      }
      memoField={
        <MemoField
          memoFieldName={`teamMembersAllocations.${teamMemberIdx}.locationGroupAllocations.${fieldIdx}.clientMemos.${client.id}`}
        />
      }
      teamsField={
        client.teams?.length && isGlobalUser ? (
          <TeamsField
            teams={client.teams}
            fieldName={`teamMembersAllocations.${teamMemberIdx}.locationGroupAllocations.${fieldIdx}.clientTeamIds.${client.id}`}
          />
        ) : undefined
      }
    />
  );
};

const AllocationLocationGroupRowItem: FC<IProps> = ({
  field,
  fieldIdx,
  teamMemberIdx,
  memberId,
  showGroupName,
  onRemoveClient,
}): ReactElement => {
  const { locationGroups, currentUserRole } = useAddClientAllocationContext();
  const { watch } = useFormContext<IAddAllocationFormValues>();

  const removedClientIds: number[] = watch(
    `teamMembersAllocations.${teamMemberIdx}.locationGroupAllocations.${fieldIdx}.removedClientIds` as const,
  );

  const allGroupClients = useMemo(
    () => locationGroups.find(lg => lg.locationGroup.id === field.locationGroupId)?.clients ?? [],
    [locationGroups, field.locationGroupId],
  );

  const visibleClients = useMemo(
    () => allGroupClients.filter(c => !(removedClientIds || []).includes(c.id)),
    [allGroupClients, removedClientIds],
  );

  return (
    <>
      {showGroupName ? (
        <Typography variant="subtitle2" className={layoutStyles.locationGroupTitle}>
          {field.locationGroupName}
        </Typography>
      ) : null}

      {visibleClients.map(client => (
        <LocationGroupClientRow
          key={client.id}
          client={client}
          memberId={memberId}
          teamMemberIdx={teamMemberIdx}
          fieldIdx={fieldIdx}
          locationGroupId={field.locationGroupId}
          isGlobalUser={currentUserRole.isGlobalUser}
          onRemove={onRemoveClient}
        />
      ))}
    </>
  );
};

export default AllocationLocationGroupRowItem;
